/**
 * Reusable clickable breadcrumb. Accepts items as either:
 *   - string  "Norea"                         → static segment
 *   - object  { label, to }                   → <Link to={to}>label</Link>
 *   - object  { label, onClick }              → button
 *
 * Automatically dims the last segment (current page) and never links it.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export default function Breadcrumb({ items }) {
  if (!items || items.length === 0) return null;

  return (
    <nav
      aria-label="Fil d'Ariane"
      className="flex items-center gap-1.5 text-xs text-norea-textMuted mb-2"
      data-testid="breadcrumb"
    >
      {items.map((raw, i) => {
        const isLast = i === items.length - 1;
        const item = typeof raw === "string" ? { label: raw } : raw || {};
        const key = `${i}-${item.label || item.to || "b"}`;

        const content = (
          <span
            className={isLast ? "text-norea-text font-medium" : ""}
            data-testid={`breadcrumb-item-${i}`}
          >
            {item.label}
          </span>
        );

        return (
          <React.Fragment key={key}>
            {i > 0 && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
            {isLast || (!item.to && !item.onClick) ? (
              content
            ) : item.to ? (
              <Link
                to={item.to}
                className="hover:text-norea-primary transition-colors"
                data-testid={`breadcrumb-link-${i}`}
              >
                {content}
              </Link>
            ) : (
              <button
                type="button"
                onClick={item.onClick}
                className="hover:text-norea-primary transition-colors"
                data-testid={`breadcrumb-link-${i}`}
              >
                {content}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
